import React, { useState, useEffect } from 'react';
import TemplatePreview from './TemplatePreview';

/**
 * Componente para formulário de edição de templates
 * Permite apenas editar o conteúdo das mensagens dos templates pré-definidos
 * 
 * @param {Object} props - Propriedades do componente
 * @param {Object} props.initialData - Dados iniciais para edição
 * @param {Function} props.onSave - Função para salvar o template
 * @param {Function} props.onCancel - Função para cancelar a edição
 * @param {Function} props.processTemplate - Função para processar variáveis no texto
 * @param {boolean} props.isMobile - Flag indicando se é visualização mobile
 * @returns {JSX.Element} Componente React
 */
const TemplateForm = ({ 
  initialData, 
  onSave, 
  onCancel, 
  processTemplate,
  isMobile = false 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    type: 'confirmacao',
    message: '',
    description: '',
    isDefault: false // mantemos no estado, mas não mostramos na UI
  });
  
  const [previewMessage, setPreviewMessage] = useState('');
  
  // Atualizar formulário quando dados iniciais mudarem
  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        type: initialData.type || 'confirmacao',
        message: initialData.message || '',
        description: initialData.description || '',
        isDefault: initialData.isDefault || false
      });
    }
  }, [initialData]);
  
  // Atualizar prévia quando a mensagem mudar
  useEffect(() => {
    setPreviewMessage(processTemplate(formData.message));
  }, [formData.message, processTemplate]);
  
  // Manipular mudanças nos campos do formulário
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Inserir variável na posição atual do cursor
  const insertVariable = (variable) => {
    const messageEl = document.getElementById('message');
    if (!messageEl) return;
    
    const start = messageEl.selectionStart;
    const end = messageEl.selectionEnd;
    const currentMessage = formData.message;
    
    const newMessage = 
      currentMessage.substring(0, start) + 
      variable + 
      currentMessage.substring(end);
    
    setFormData(prev => ({
      ...prev,
      message: newMessage
    }));
    
    // Reposicionar cursor após a variável inserida
    setTimeout(() => {
      messageEl.focus();
      messageEl.setSelectionRange(start + variable.length, start + variable.length);
    }, 10);
  };
  
  // Tipos de templates disponíveis
  const templateTypes = [
    { value: 'confirmacao', label: 'Confirmação' },
    { value: 'lembrete', label: 'Lembrete' },
    { value: 'agradecimento', label: 'Agradecimento' },
    { value: 'cancelamento', label: 'Cancelamento' },
    { value: 'reagendamento', label: 'Reagendamento' },
    { value: 'aniversario', label: 'Aniversário' },
    { value: 'promocao', label: 'Promoção' }
  ];
  
  /**
   * Lista de variáveis disponíveis para substituição nos templates
   */
  const TEMPLATE_VARIABLES = [
    { label: 'Nome do Cliente', variable: '{cliente}' },
    { label: 'Nome do Serviço', variable: '{servico}' },
    { label: 'Data do Agendamento', variable: '{data}' },
    { label: 'Horário do Agendamento', variable: '{hora}' },
    { label: 'Nome do Profissional', variable: '{profissional}' },
    { label: 'Valor do Serviço', variable: '{valor}' },
    { label: 'Nome do Salão', variable: '{salao}' },
    { label: 'Telefone do Cliente', variable: '{telefone}' },
    { label: 'Email do Cliente', variable: '{email}' }
  ];
  
  /**
   * Novas variáveis para links dinâmicos
   */
  const LINK_VARIABLES = [
    { label: 'Link para Confirmar', variable: '{link_confirmar}' },
    { label: 'Link para Reagendar', variable: '{link_reagendar}' },
    { label: 'Link para Cancelar', variable: '{link_cancelar}' }
  ];
  
  // Validar formulário antes de salvar
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.message.trim()) {
      alert('Por favor, informe uma mensagem para o template.');
      return;
    }
    
    onSave(formData);
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Coluna de formulário */}
        <div className="space-y-4">
          <div>
            <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de Mensagem
            </label>
            <div className="px-3 py-2 border border-gray-200 bg-gray-50 rounded-md text-gray-700 font-medium">
              {templateTypes.find(t => t.value === formData.type)?.label || formData.type}
            </div>
          </div>
          
          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
              Mensagem*
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={10}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder="Digite aqui sua mensagem e insira as variáveis disponíveis..."
              required
            ></textarea>
          </div>
          
          {/* Área de variáveis disponíveis */}
          <div className="mt-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              Variáveis Disponíveis
            </h3>
            <div className="flex flex-wrap gap-2">
              {TEMPLATE_VARIABLES.map((varItem) => (
                <button
                  key={varItem.variable}
                  type="button"
                  onClick={() => insertVariable(varItem.variable)}
                  className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {varItem.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Nova área para links dinâmicos */}
          <div className="mt-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              Links para Ações (novo)
            </h3>
            <div className="flex flex-wrap gap-2">
              {LINK_VARIABLES.map((varItem) => (
                <button
                  key={varItem.variable}
                  type="button"
                  onClick={() => insertVariable(varItem.variable)}
                  className="inline-flex items-center px-2.5 py-1.5 border border-blue-300 text-xs font-medium rounded text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {varItem.label}
                </button>
              ))}
            </div>
            
            <div className="mt-2 bg-blue-50 p-3 rounded-md">
              <h4 className="font-medium text-blue-800 text-sm">Como usar os links:</h4>
              <p className="text-sm text-blue-700 mt-1">
                Adicione os links de ação nos seus templates para permitir que os clientes confirmem, reagendem ou cancelem o agendamento diretamente pelo WhatsApp.
              </p>
              <p className="text-sm text-blue-700 mt-1">
                <strong>Exemplo:</strong> "Confirme seu agendamento aqui: {"{link_confirmar}"}"
              </p>
            </div>
          </div>
        </div>
        
        {/* Coluna de prévia (apenas em desktop) */}
        {!isMobile && (
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Prévia da Mensagem</h3>
            <TemplatePreview message={previewMessage} />
          </div>
        )}
      </div>
      
      {/* Prévia em mobile (exibida abaixo do formulário) */}
      {isMobile && (
        <div className="mt-6">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Prévia da Mensagem</h3>
          <TemplatePreview message={previewMessage} />
        </div>
      )}
      
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Cancelar
        </button>
        <button
          type="submit"
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Salvar
        </button>
      </div>
    </form>
  );
};

export default TemplateForm;
